import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { authFetch } from '../../utils/api';

const { Title, Paragraph, Text } = Typography;

interface DecoyConnectionRow {
  id: number;
  brand: string;
  userId: number | null;
  username: string | null;
  nodeId: number;
  sourceIp: string | null;
  tokenDigest: string | null;
  tokenKind: string | null;
  headers: string | null;
  requestedAt: string;
}

interface DecoySuspect {
  userId: number | null;
  username: string | null;
  brand: string;
  hits: number;
  lastHit: string;
}

const TOKEN_KIND_COLORS: Record<string, string> = {
  jwt: 'gold',
  'connect-ticket': 'magenta',
  unknown: 'default',
};

function formatTime(value: string | null | undefined): string {
  if (!value) return '-';
  const d = dayjs(value);
  if (!d.isValid()) return String(value);
  return d.format('YYYY-MM-DD HH:mm:ss');
}

export const DecoyConnectionsPage: React.FC = () => {
  const [hits, setHits] = useState<DecoyConnectionRow[]>([]);
  const [hitsTotal, setHitsTotal] = useState(0);
  const [hitsPage, setHitsPage] = useState(1);
  const [hitsPageSize, setHitsPageSize] = useState(50);
  const [hitsLoading, setHitsLoading] = useState(false);

  const [suspects, setSuspects] = useState<DecoySuspect[]>([]);
  const [suspectsLoading, setSuspectsLoading] = useState(false);

  const [since, setSince] = useState<string | undefined>(() => {
    // Default last 7 days
    const past = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return past.toISOString();
  });

  const fetchHits = useCallback(async () => {
    setHitsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(hitsPage),
        pageSize: String(hitsPageSize),
      });
      if (since) params.set('since', since);
      const res = await authFetch(
        `/api/admin/decoy-connections?${params.toString()}`
      );
      const result = await res.json();
      if (result.code === 0 && result.data) {
        setHits(Array.isArray(result.data.list) ? result.data.list : []);
        setHitsTotal(result.data.total ?? 0);
      } else {
        message.warning(result.msg || 'decoy hits 查詢失敗');
      }
    } catch {
      message.error('decoy hits 查詢失敗');
    } finally {
      setHitsLoading(false);
    }
  }, [hitsPage, hitsPageSize, since]);

  const fetchSuspects = useCallback(async () => {
    setSuspectsLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (since) params.set('since', since);
      const res = await authFetch(
        `/api/admin/decoy-connections/suspects?${params.toString()}`
      );
      const result = await res.json();
      if (result.code === 0 && Array.isArray(result.data)) {
        setSuspects(result.data);
      } else {
        message.warning(result.msg || 'decoy suspects 查詢失敗');
      }
    } catch {
      message.error('decoy suspects 查詢失敗');
    } finally {
      setSuspectsLoading(false);
    }
  }, [since]);

  useEffect(() => {
    fetchHits();
  }, [fetchHits]);

  useEffect(() => {
    fetchSuspects();
  }, [fetchSuspects]);

  const items = useMemo(
    () => [
      {
        key: 'hits',
        label: `Hit 列表 (${hitsTotal})`,
        children: (
          <Table
            dataSource={hits}
            loading={hitsLoading}
            rowKey="id"
            data-testid="decoy-hits-table"
            scroll={{ x: 1200 }}
            pagination={{
              current: hitsPage,
              pageSize: hitsPageSize,
              total: hitsTotal,
              showTotal: (t) => `共 ${t} 筆`,
              onChange: (p, ps) => {
                setHitsPage(p);
                setHitsPageSize(ps);
              },
            }}
          >
            <Table.Column dataIndex="id" title="ID" width={70} />
            <Table.Column
              dataIndex="requestedAt"
              title="觸發時間"
              width={160}
              render={(t: string) => formatTime(t)}
            />
            <Table.Column dataIndex="brand" title="品牌" width={90} />
            <Table.Column
              title="用戶"
              width={160}
              render={(_: unknown, row: DecoyConnectionRow) =>
                row.userId ? (
                  <Space>
                    <Tag color="red">#{row.userId}</Tag>
                    <Text>{row.username ?? '-'}</Text>
                  </Space>
                ) : (
                  <Tag>未知</Tag>
                )
              }
            />
            <Table.Column dataIndex="nodeId" title="蜜罐節點" width={100} />
            <Table.Column
              dataIndex="sourceIp"
              title="來源 IP"
              width={140}
              render={(ip: string | null) => (
                <Text code>{ip ?? '-'}</Text>
              )}
            />
            <Table.Column
              dataIndex="tokenKind"
              title="token 類型"
              width={120}
              render={(kind: string | null) => (
                <Tag color={kind ? TOKEN_KIND_COLORS[kind] || 'default' : 'default'}>
                  {kind ?? 'unknown'}
                </Tag>
              )}
            />
            <Table.Column
              dataIndex="tokenDigest"
              title="token 摘要"
              width={220}
              render={(d: string | null) =>
                d ? (
                  <Text code copyable>
                    {d}
                  </Text>
                ) : (
                  '-'
                )
              }
            />
            <Table.Column
              dataIndex="headers"
              title="headers"
              width={300}
              render={(h: string | null) =>
                h ? (
                  <details>
                    <summary>展開</summary>
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {h}
                    </pre>
                  </details>
                ) : (
                  '-'
                )
              }
            />
          </Table>
        ),
      },
      {
        key: 'suspects',
        label: `可疑帳號 (${suspects.length})`,
        children: (
          <Table
            dataSource={suspects}
            loading={suspectsLoading}
            rowKey={(row: DecoySuspect) =>
              `${row.userId ?? 'null'}:${row.brand}`
            }
            data-testid="decoy-suspects-table"
            pagination={{ pageSize: 50 }}
          >
            <Table.Column
              dataIndex="userId"
              title="用戶 ID"
              width={120}
              render={(id: number | null) =>
                id ? <Tag color="red">#{id}</Tag> : <Tag>未知</Tag>
              }
            />
            <Table.Column
              dataIndex="username"
              title="帳號"
              width={200}
              render={(name: string | null) => name ?? '-'}
            />
            <Table.Column dataIndex="brand" title="品牌" width={100} />
            <Table.Column
              dataIndex="hits"
              title="觸發次數"
              width={120}
              sorter={(a: DecoySuspect, b: DecoySuspect) => a.hits - b.hits}
              render={(hits: number) => (
                <Tag color={hits >= 3 ? 'red' : 'orange'}>{hits}</Tag>
              )}
            />
            <Table.Column
              dataIndex="lastHit"
              title="最後觸發"
              width={180}
              render={(t: string) => formatTime(t)}
            />
          </Table>
        ),
      },
    ],
    [hits, hitsLoading, hitsPage, hitsPageSize, hitsTotal, suspects, suspectsLoading]
  );

  return (
    <div className="page-enter" data-testid="decoy-connections-page">
      <Title level={4} style={{ marginBottom: 12 }}>
        蜜罐節點觸發紀錄 (AR-Q7)
      </Title>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="這張表的每一筆都是逆向工程訊號"
        description={
          <Paragraph style={{ marginBottom: 0 }}>
            真實 K盾 客戶端永遠不會打到 <Text code>is_decoy=true</Text> 節點 ——
            因為 manifest 已經把它過濾掉。任何打進這張表的 user
            幾乎 100% 是反組譯 binary 抽 endpoint 字串、或直接 poke API
            的逆向工程者。建議搭配 <Text code>vpn_client_log</Text> 中的
            root / Frida flag 做關聯，再在用戶頁直接停權或封鎖
            device fingerprint。
          </Paragraph>
        }
      />
      <Space style={{ marginBottom: 12 }}>
        <Text type="secondary">時間範圍從：</Text>
        <DatePicker
          showTime
          value={since ? dayjs(since) : null}
          onChange={(date) =>
            setSince(date ? date.toISOString() : undefined)
          }
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            fetchHits();
            fetchSuspects();
          }}
          data-testid="decoy-refresh-btn"
        >
          重新整理
        </Button>
      </Space>
      <Tabs items={items} data-testid="decoy-connections-tabs" />
    </div>
  );
};

export default DecoyConnectionsPage;
